import { cloneElement } from 'react'
import Styles from './WorkflowMomentModal.module.scss'
import cx from 'classnames'
import Avatar from 'common/Avatar/Avatar'

import { WorkflowMomentModalProps } from './Types'
import { ReactComponent as ArrowRight } from 'assets/icons/arrowRight24.svg'
import { ReactComponent as AlmanacLogo } from 'assets/icons/lockup--landscape.svg'
import { AvatarsStack } from 'components/AvatarsStack/AvatarsStack'
import { Modal } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Underlay } from 'almanac-ui/Modal/Underlay'

const WorkflowMomentModal = ({
  setVisible,
  onClose,
  header,
  title,
  description,
  nextActor,
  primaryCta,
  secondaryCta,
  content,
  heroUrl,
  hero,
  hint,
  avatarsEmoji,
  leftAvatar,
  rightAvatar,
  leftAvatarRingColor,
  rightAvatarRingColor,
  contentClassName,
  footer,
  showAvatars = true,
  v2 = false,
  disableClose = false,
}: WorkflowMomentModalProps) => {
  const { isMobile } = useResponsiveDesign()
  const handleClose = () => {
    if (disableClose) return
    onClose?.()
    setVisible(false)
  }

  const avatarsConnector = () => {
    if (avatarsEmoji) {
      return (
        <div className={Styles.avatarsEmoji}>
          <span role="img" aria-label="">
            {avatarsEmoji}
          </span>
        </div>
      )
    }

    return <ArrowRight className={cx(Styles.avatarArrow, { [Styles.leftArrow]: nextActor === 'left' })} />
  }

  const renderRightAvatars = () => {
    if (!rightAvatar) {
      return null
    }

    if (Array.isArray(rightAvatar)) {
      return (
        <AvatarsStack
          size={24}
          avatars={rightAvatar.map(({ initials, url }) => ({ url, label: initials }))}
          visibleCount={3}
          strokeColor={rightAvatarRingColor}
        />
      )
    } else {
      return (
        <div
          className={cx(Styles.avatarRing, { [Styles.faintAvatar]: nextActor === 'left' })}
          style={{ borderColor: rightAvatarRingColor }}
        >
          <Avatar size={24} label={rightAvatar.initials} url={rightAvatar.url} />
        </div>
      )
    }
  }

  return (
    <>
      <Underlay isVisible />
      <Modal.Root
        onPressOutside={handleClose}
        onClose={handleClose}
        wrapperClassName={cx(Styles.container, { [Styles.v2]: v2 })}
        size="sm"
      >
        <Modal.Header onClose={handleClose}>
          <div className={Styles.header}>{v2 ? header : <div className={Styles.headerContent}>{header}</div>}</div>
        </Modal.Header>
        <Modal.Content>
          <div className={Styles.body}>
            {hero ? hero : heroUrl && <img src={heroUrl} className={Styles.hero} alt="" />}
            <div className={cx(Styles.content, contentClassName)}>
              {showAvatars && (
                <div className={Styles.avatars}>
                  {leftAvatar && (
                    <div
                      className={cx(Styles.avatarRing, { [Styles.faintAvatar]: nextActor === 'right' })}
                      style={{ borderColor: leftAvatarRingColor }}
                    >
                      <Avatar size={24} label={leftAvatar.initials} url={leftAvatar.url} />
                    </div>
                  )}
                  {avatarsConnector()}
                  {rightAvatar && renderRightAvatars()}
                </div>
              )}
              <h2 className={Styles.title}>{title}</h2>
              {description && <p className={Styles.description}>{description}</p>}
              {content}
              {hint && !isMobile && <p className={Styles.hint}>{hint}</p>}
            </div>
          </div>
        </Modal.Content>
        {!footer && (
          <Modal.Footer vertical>
            <Modal.FooterContent slot="right">{primaryCta && <div>{primaryCta}</div>}</Modal.FooterContent>
            <Modal.FooterContent slot="left">{secondaryCta && <div>{secondaryCta}</div>}</Modal.FooterContent>
          </Modal.Footer>
        )}
        {footer && !v2 && cloneElement(footer, { className: cx(Styles.footer, footer.props.className) })}
        {footer && v2 && (
          <footer className={Styles.footer}>
            <AlmanacLogo className={Styles.almanacLogo} />
            {cloneElement(footer, {})}
          </footer>
        )}
      </Modal.Root>
    </>
  )
}

export default WorkflowMomentModal
